import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { formatSegnalazioneDisplay } from "../../utils/diarioSegnalazioniDisplay";
import {
  BookOpen,
  Plus,
  Calendar,
  Clock,
  User,
  Sun,
  Sunset,
  Moon,
  AlertCircle,
  RefreshCw,
  X,
  ChevronDown,
  ChevronUp,
  Filter,
  Printer,
  Pencil,
  Trash2,
} from "lucide-react";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { useOssAuth } from "../../context/OssAuthContext";
import OssSectionPageHeader from "../../components/oss/OssSectionPageHeader";
import OssPrintDocumentHeader from "../../components/oss/OssPrintDocumentHeader";
import { VoiceDictateButton } from "../../components/VoiceDictateButton";
import { printOssPage } from "../../lib/oss/print";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TURNI = {
  mattina:    { name: "Mattina",    icon: Sun,    orario: "06:00 - 14:00", color: "text-amber-500",  bg: "bg-amber-100" },
  pomeriggio: { name: "Pomeriggio", icon: Sunset, orario: "14:00 - 22:00", color: "text-orange-500", bg: "bg-orange-100" },
  notte:      { name: "Notte",      icon: Moon,   orario: "22:00 - 06:00", color: "text-indigo-500", bg: "bg-indigo-100" },
};

function formatDate(dateStr) {
  if (!dateStr) return "N/D";
  try {
    return new Date(dateStr).toLocaleDateString("it-IT", {
      weekday: "long", day: "2-digit", month: "long", year: "numeric",
    });
  } catch { return dateStr; }
}

function formatTime(isoStr) {
  if (!isoStr) return "";
  try {
    return new Date(isoStr).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}

export default function OssDiarioConsegne() {
  const { token } = useOssAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNewForm, setShowNewForm] = useState(false);
  const [filterTurno, setFilterTurno] = useState("all");
  // filterDataInput: valore digitato (non scatena fetch)
  // filterDataApplied: valore applicato (scatena fetch) — si aggiorna al change del selettore data
  const [filterDataInput, setFilterDataInput] = useState("");
  const [filterDataApplied, setFilterDataApplied] = useState("");
  const [expandedEntries, setExpandedEntries] = useState(new Set());

  const [newConsegna, setNewConsegna] = useState({
    data: new Date().toISOString().split("T")[0],
    turno: "mattina",
    segnalazioni: [],
  });
  const [nuovaConsegnaKey, setNuovaConsegnaKey] = useState(0);
  const [contenutoValue, setContenutoValue] = useState("");
  const [newSegnalazioneValue, setNewSegnalazioneValue] = useState("");
  const [saving, setSaving] = useState(false);

  const [editOpen, setEditOpen] = useState(false);
  const [editEntryId, setEditEntryId] = useState(null);
  const [editContenuto, setEditContenuto] = useState("");
  const [editSegnalazioni, setEditSegnalazioni] = useState([]);
  const [editSegInput, setEditSegInput] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  const fetchEntries = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterTurno !== "all") params.append("turno", filterTurno);
      if (filterDataApplied) params.append("data_da", filterDataApplied);
      params.append("limit", "50");
      const res = await axios.get(
        `${API_URL}/api/oss/me/diario-consegne?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEntries(res.data.entries || []);
    } catch {
      toast.error("Errore nel caricamento delle consegne");
    } finally {
      setLoading(false);
    }
  }, [token, filterTurno, filterDataApplied]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const handlePrint = () => {
    printOssPage("Diario consegne");
  };

  const handleAddSegnalazione = () => {
    const t = newSegnalazioneValue.trim();
    if (!t) return;
    setNewConsegna((prev) => ({
      ...prev,
      segnalazioni: [...prev.segnalazioni, t],
    }));
    setNewSegnalazioneValue("");
  };

  const handleRemoveSegnalazione = (index) => {
    setNewConsegna((prev) => ({
      ...prev,
      segnalazioni: prev.segnalazioni.filter((_, i) => i !== index),
    }));
  };

  const handleCreateConsegna = async () => {
    const contenuto = contenutoValue.trim();
    if (!contenuto) {
      toast.error("Inserisci il contenuto della consegna");
      return;
    }
    setSaving(true);
    try {
      await axios.post(
        `${API_URL}/api/oss/me/diario-consegne`,
        { ...newConsegna, contenuto },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Consegna registrata");
      setShowNewForm(false);
      setNewConsegna({
        data: new Date().toISOString().split("T")[0],
        turno: "mattina",
        segnalazioni: [],
      });
      setContenutoValue("");
      setNewSegnalazioneValue("");
      fetchEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nella registrazione");
    } finally {
      setSaving(false);
    }
  };

  const toggleExpand = (id) => {
    setExpandedEntries((prev) => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });
  };

  const openEdit = (entry) => {
    setEditEntryId(entry.id);
    setEditContenuto(entry.contenuto || "");
    setEditSegnalazioni(Array.isArray(entry.segnalazioni) ? [...entry.segnalazioni] : []);
    setEditSegInput("");
    setEditOpen(true);
  };

  const closeEdit = () => {
    setEditOpen(false);
    setEditEntryId(null);
    setEditContenuto("");
    setEditSegnalazioni([]);
    setEditSegInput("");
  };

  const handleSaveEdit = async () => {
    const contenuto = editContenuto.trim();
    if (!contenuto) {
      toast.error("Inserisci il contenuto della consegna");
      return;
    }
    if (!editEntryId || !token) return;
    setSavingEdit(true);
    try {
      await axios.patch(
        `${API_URL}/api/oss/me/diario-consegne/${editEntryId}`,
        { contenuto, segnalazioni: editSegnalazioni },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Consegna aggiornata");
      closeEdit();
      fetchEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Impossibile aggiornare la consegna");
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeleteEntry = async (entryId) => {
    if (!token) return;
    if (!window.confirm("Eliminare questa consegna? L'operazione non è annullabile.")) return;
    try {
      await axios.delete(`${API_URL}/api/oss/me/diario-consegne/${entryId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Consegna eliminata");
      fetchEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Impossibile eliminare la consegna");
    }
  };

  const addEditSegnalazione = () => {
    const text = editSegInput.trim();
    if (!text) return;
    setEditSegnalazioni((prev) => [...prev, text]);
    setEditSegInput("");
  };

  const removeEditSegnalazione = (index) => {
    setEditSegnalazioni((prev) => prev.filter((_, i) => i !== index));
  };

  const groupedEntries = entries.reduce((acc, entry) => {
    const date = entry.data || "Sconosciuta";
    if (!acc[date]) acc[date] = [];
    acc[date].push(entry);
    return acc;
  }, {});

  const printStampaIl = new Date().toLocaleDateString("it-IT", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
  const dataFiltroShort = filterDataApplied
    ? new Date(`${filterDataApplied}T12:00:00`).toLocaleDateString("it-IT")
    : null;
  const turnoFiltroLabel =
    filterTurno === "all"
      ? "Tutti i turni"
      : (TURNI[filterTurno]?.name ?? filterTurno);

  return (
    <div>
      <div className="print:hidden">
        <OssSectionPageHeader
          sectionKey="diarioConsegne"
          title="Diario Consegne"
          subtitle="Diario generale della struttura per le consegne tra turni"
          toolbar={
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-2">
                <Filter className="w-4 h-4 text-slate-400" />
                <Select value={filterTurno} onValueChange={setFilterTurno}>
                  <SelectTrigger className="w-[140px] h-11 bg-white/80">
                    <SelectValue placeholder="Turno" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti i turni</SelectItem>
                    {Object.entries(TURNI).map(([key, { name }]) => (
                      <SelectItem key={key} value={key}>{name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  type="date"
                  value={filterDataInput}
                  onChange={(e) => {
                    // il selettore data touch non sempre fa blur: applica subito (il valore è sempre completo o vuoto)
                    setFilterDataInput(e.target.value);
                    setFilterDataApplied(e.target.value);
                  }}
                  className="w-[150px] h-11 bg-white/80"
                />
                <Button variant="ghost" size="sm" className="h-11 w-11 p-0" onClick={fetchEntries} disabled={loading} aria-label="Aggiorna">
                  <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                </Button>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button type="button" variant="outline" className="border-blue-200 text-blue-800 hover:bg-blue-50" onClick={handlePrint}>
                  <Printer className="w-4 h-4 mr-2" />
                  Stampa
                </Button>
                <Button
                  onClick={() => {
                    setNuovaConsegnaKey((k) => k + 1);
                    setNewConsegna({
                      data: new Date().toISOString().split("T")[0],
                      turno: "mattina",
                      segnalazioni: [],
                    });
                    setContenutoValue("");
                    setNewSegnalazioneValue("");
                    setShowNewForm(true);
                  }}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Nuova Consegna
                </Button>
              </div>
            </div>
          }
        />
      </div>

      <OssPrintDocumentHeader
        kind="Diario consegne"
        title="Registro consegne tra turni"
        lines={[
          dataFiltroShort ? `Da data: ${dataFiltroShort}` : "Nessun filtro sulla data (elenco recente)",
          `Turno: ${turnoFiltroLabel}`,
          `Stampa: ${printStampaIl}`,
        ]}
      />

      {/* New consegna form */}
      {showNewForm && (
        <Card className="mb-6 border-blue-200 bg-blue-50/50 print:hidden">
          <CardContent className="p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-medium text-blue-800">Nuova Consegna</h4>
              <Button variant="ghost" size="sm" onClick={() => setShowNewForm(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Data</Label>
                <Input
                  type="date"
                  value={newConsegna.data}
                  onChange={(e) => setNewConsegna((prev) => ({ ...prev, data: e.target.value }))}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Turno</Label>
                <Select
                  value={newConsegna.turno}
                  onValueChange={(v) => setNewConsegna((prev) => ({ ...prev, turno: v }))}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(TURNI).map(([key, { name, orario }]) => (
                      <SelectItem key={key} value={key}>{name} ({orario})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Descrizione turno</Label>
              <div className="flex gap-2 mt-1 items-start">
                <Textarea
                  value={contenutoValue}
                  onChange={(e) => setContenutoValue(e.target.value)}
                  placeholder="Descrivi come è andato il turno, eventi significativi..."
                  rows={4}
                  className="resize-none flex-1"
                />
                <VoiceDictateButton
                  value={contenutoValue}
                  onChange={setContenutoValue}
                  onTurnoChange={(turno) => setNewConsegna((prev) => ({ ...prev, turno }))}
                  onDataChange={(data) => setNewConsegna((prev) => ({ ...prev, data }))}
                />
              </div>
            </div>
            <div>
              <Label>Segnalazioni da passare</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={newSegnalazioneValue}
                  onChange={(e) => setNewSegnalazioneValue(e.target.value)}
                  placeholder="Aggiungi una segnalazione..."
                  className="flex-1"
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleAddSegnalazione())}
                />
                <VoiceDictateButton
                  value={newSegnalazioneValue}
                  onChange={setNewSegnalazioneValue}
                  showInterim={false}
                />
                <Button type="button" variant="outline" onClick={handleAddSegnalazione}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {newConsegna.segnalazioni.length > 0 && (
                <div className="mt-2 space-y-1">
                  {newConsegna.segnalazioni.map((seg, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm bg-amber-50 border border-amber-200 rounded px-3 py-1">
                      <AlertCircle className="w-4 h-4 text-amber-600 shrink-0" />
                      <span className="flex-1">{formatSegnalazioneDisplay(seg)}</span>
                      <Button variant="ghost" size="sm" className="h-11 w-11 p-0" aria-label="Rimuovi segnalazione" onClick={() => handleRemoveSegnalazione(i)}>
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowNewForm(false)}>Annulla</Button>
              <Button
                onClick={handleCreateConsegna}
                disabled={saving}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {saving ? "Salvataggio..." : "Registra Consegna"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Entries list */}
      {loading ? (
        <div className="text-center py-12 text-slate-500 print:hidden">
          <RefreshCw className="w-6 h-6 mx-auto mb-2 animate-spin" />
          Caricamento...
        </div>
      ) : entries.length === 0 ? (
        <div className="text-center py-16 text-slate-500 print:py-8">
          <BookOpen className="w-12 h-12 mx-auto mb-4 text-slate-300 print:hidden" />
          <p>Nessuna consegna registrata</p>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedEntries)
            .sort(([a], [b]) => b.localeCompare(a))
            .map(([date, dayEntries]) => (
              <div key={date}>
                <div className="flex items-center gap-2 mb-3 sticky top-[64px] bg-slate-100 py-2 z-10 print:static print:bg-transparent print:py-1">
                  <Calendar className="w-4 h-4 text-slate-400" />
                  <span className="font-semibold text-slate-700">{formatDate(date)}</span>
                  <Badge variant="outline" className="text-xs">
                    {dayEntries.length} {dayEntries.length === 1 ? "consegna" : "consegne"}
                  </Badge>
                </div>
                <div className="space-y-3 pl-6 border-l-2 border-blue-200">
                  {dayEntries.map((entry) => {
                    const turnoConfig = TURNI[entry.turno] || TURNI.mattina;
                    const TurnoIcon = turnoConfig.icon;
                    const isExpanded = expandedEntries.has(entry.id);
                    const testo = entry.contenuto || "";
                    const isLong = testo.length > 200;

                    return (
                      <Card key={entry.id} className="card-base print:break-inside-avoid print:shadow-none print:border print:border-slate-300">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between gap-2 mb-3">
                            <div className="flex items-center gap-2 flex-wrap min-w-0">
                              <Badge className={`${turnoConfig.bg} ${turnoConfig.color} border-0`}>
                                <TurnoIcon className="w-3 h-3 mr-1" />
                                {turnoConfig.name}
                              </Badge>
                              <span className="text-xs text-slate-500">{turnoConfig.orario}</span>
                            </div>
                            <div className="flex items-center gap-1 shrink-0 print:hidden">
                              {entry.authorCanModify ? (
                                <>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="h-11 w-11 p-0 text-slate-600"
                                    onClick={() => openEdit(entry)}
                                    title="Modifica"
                                  >
                                    <Pencil className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="h-11 w-11 p-0 text-rose-600 hover:text-rose-700"
                                    onClick={() => handleDeleteEntry(entry.id)}
                                    title="Elimina"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </>
                              ) : null}
                              <span className="text-xs text-slate-400 flex items-center gap-1 ml-0.5">
                                <Clock className="w-3 h-3" />
                                {formatTime(entry.createdAt)}
                              </span>
                            </div>
                          </div>
                          <p
                            className={`text-slate-700 whitespace-pre-wrap print:line-clamp-none ${!isExpanded && isLong ? "line-clamp-3" : ""}`}
                          >
                            {testo}
                          </p>
                          {isLong && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => toggleExpand(entry.id)}
                              className="mt-1 h-6 text-xs text-blue-600 print:hidden"
                            >
                              {isExpanded ? (
                                <><ChevronUp className="w-3 h-3 mr-1" />Comprimi</>
                              ) : (
                                <><ChevronDown className="w-3 h-3 mr-1" />Leggi tutto</>
                              )}
                            </Button>
                          )}
                          {entry.segnalazioni && entry.segnalazioni.length > 0 && (
                            <div className="mt-3 space-y-1">
                              <span className="text-xs font-medium text-amber-700">Segnalazioni:</span>
                              {entry.segnalazioni.map((seg, i) => (
                                <div key={i} className="flex items-center gap-2 text-sm bg-amber-50 border border-amber-200 rounded px-3 py-1">
                                  <AlertCircle className="w-4 h-4 text-amber-600 shrink-0" />
                                  <span>{formatSegnalazioneDisplay(seg)}</span>
                                </div>
                              ))}
                            </div>
                          )}
                          <div className="flex items-center gap-2 mt-3 text-xs text-slate-500">
                            <User className="w-3 h-3" />
                            <span>{entry.operatoreNome || "Operatore"}</span>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            ))}
        </div>
      )}
      <Dialog open={editOpen} onOpenChange={(open) => { if (!open) closeEdit(); }}>
        <DialogContent className="print:hidden max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Modifica consegna</DialogTitle>
            <DialogDescription>
              Modifica o elimina dalla lista solo le consegne da te inserite, fino alla fine del turno (fuso Italia).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Descrizione turno</Label>
              <Textarea
                value={editContenuto}
                onChange={(e) => setEditContenuto(e.target.value)}
                rows={5}
                className="mt-1 resize-none"
              />
            </div>
            <div>
              <Label>Segnalazioni da passare</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={editSegInput}
                  onChange={(e) => setEditSegInput(e.target.value)}
                  placeholder="Aggiungi una segnalazione..."
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addEditSegnalazione();
                    }
                  }}
                />
                <Button type="button" variant="outline" onClick={addEditSegnalazione}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {editSegnalazioni.length > 0 && (
                <div className="mt-2 space-y-1">
                  {editSegnalazioni.map((seg, index) => (
                    <div key={index} className="flex items-center gap-2 text-sm bg-amber-50 border border-amber-200 rounded px-3 py-1">
                      <AlertCircle className="w-4 h-4 text-amber-600 shrink-0" />
                      <span className="flex-1">{formatSegnalazioneDisplay(seg)}</span>
                      <Button variant="ghost" size="sm" className="h-11 w-11 p-0" aria-label="Rimuovi segnalazione" onClick={() => removeEditSegnalazione(index)}>
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={closeEdit}>
              Annulla
            </Button>
            <Button type="button" onClick={handleSaveEdit} disabled={savingEdit} className="bg-blue-600 hover:bg-blue-700">
              {savingEdit ? "Salvataggio..." : "Salva modifiche"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
